/* eslint-disable indent */
import { FieldValues } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { TableCell } from '@/components/Datatable/TableCell';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { TableMenu } from '@/components/Menu/TableMenu';
import { StatusTag } from '@/components/Tag/StatusTag';
import { IDatatableData } from '@/type/listing.types';
import { getFormattedDate, getFormattedTime } from '@/utils/date.utils';
import {
  getInspectionProgressStatusLabel,
  getInspectionSubProgressStatus,
} from '@/utils/vehicule-check';
import { SmallInfoContent } from '@/components/SmallInfoContent';
import { useUserRole } from '@/hooks/auth.hooks';

export const InspectionUsageTableData = <TSchema extends FieldValues>({
  t,
  data,
}: IDatatableData<TSchema>) => {
  const { isAdmin, isModerator, isGroupManager, isCompanyManager } =
    useUserRole();

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {data.map((entry: TSchema) => {
        const licensePlateNumber =
          entry?.vehiculeCheckResult?.licensePlateNumber;
        const vin = entry?.vehiculeCheckResult?.vin;

        const groups = entry?.user?.entityRoles
          ?.filter((role) => role?.group)
          ?.map((role) => role?.group);
        const companies = entry?.user?.entityRoles
          ?.filter((role) => role?.company)
          ?.map((role) => role?.company);
        const branches = entry?.user?.entityRoles
          ?.filter((role) => role?.branch)
          ?.map((role) => role?.branch);

        const groupsExist = !!groups?.length;
        const companiesExist = !!companies?.length;
        const branchesExist = !!branches?.length;

        const isIndividual =
          entry?.actionUserType === ActionUserType.INDIVIDUAL;

        const isToModerate =
          entry?.humanModerationStatus === HumanModerationStatus.A_MODERER;

        const isSubProgressStatusCompleted = entry?.progressStatus
          ?.toLowerCase()
          .includes('completed_');

        const subProgressStatus = getInspectionSubProgressStatus({
          t,
          status: entry?.progressStatus,
        });

        return (
          <tr key={entry?.id}>
            <TableCell
              content={
                <div className="flex flex-col">
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-calendar text-md" />
                    </div>
                    <div>{getFormattedDate(entry?.updatedAt)}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-time text-md" />
                    </div>
                    <div>{getFormattedTime(entry?.updatedAt)}</div>
                  </div>
                </div>
              }
            />
            <TableCell
              content={
                <>
                  <div className="flex gap-2">
                    <StatusTag
                      text="123"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] font-medium">
                      {licensePlateNumber || '--'}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <StatusTag
                      text="VIN"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] break-all">{vin || '--'}</div>
                  </div>
                </>
              }
            />
            <TableCell
              tdClassName="min-w-[135px] text-[13.7px] break-all"
              content={entry?.referenceNumber || '--'}
            />
            <TableCell
              content={
                <div>
                  <div className="font-medium">
                    {entry?.user?.firstname && entry?.user?.lastname
                      ? `${
                          entry?.user?.firstname
                        } ${entry?.user?.lastname?.toUpperCase()}`
                      : '--'}
                  </div>
                  {(groupsExist || companiesExist || branchesExist) && (
                    <div className="mt-1.5 flex flex-col gap-1">
                      {(isAdmin || isModerator || isGroupManager) &&
                        groupsExist && (
                          <SmallInfoContent
                            label={
                              groups?.length > 1
                                ? t('common:groups')
                                : t('common:group')
                            }
                            value={groups
                              ?.map((group) => group?.name)
                              ?.join(', ')}
                          />
                        )}
                      {(isAdmin || isModerator || isCompanyManager) &&
                        companiesExist && (
                          <SmallInfoContent
                            label={
                              companies?.length > 1
                                ? t('common:companies')
                                : t('common:company')
                            }
                            value={companies
                              ?.map((company) => company?.name)
                              ?.join(', ')}
                          />
                        )}
                      {branchesExist && (
                        <SmallInfoContent
                          label={
                            branches?.length > 1
                              ? t('common:branches')
                              : t('common:branch')
                          }
                          value={branches
                            ?.map((branch) => branch?.name)
                            ?.join(', ')}
                        />
                      )}
                    </div>
                  )}
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex justify-center items-center">
                  <div className="px-3 py-2.5 min-w-[62px] bg-slate-50 rounded-lg font-semibold text-[13.3px] tracking-wide text-center uppercase">
                    {isIndividual
                      ? t('common:individual_short')
                      : t('common:professional_short')}
                  </div>
                </div>
              }
            />
            <TableCell
              tdClassName="!px-2.5 text-center"
              content={
                <StatusTag
                  text={`${
                    isToModerate
                      ? t('common:toModerate')
                      : t('common:moderated_feminine')
                  }`}
                  className={
                    isToModerate
                      ? 'bg-red-FF2929/10 text-red-FF2929'
                      : 'bg-green-EAFFF2 border-green-C9F4D2 py-[3px] text-green-11BFA4 text-[12.7px] tracking-[0.005em]'
                  }
                />
              }
            />
            <TableCell
              tdClassName="text-center !px-2.5"
              content={
                <>
                  <StatusTag
                    className="bg-purple-EFEFFD py-[6px] px-[7.5px] text-indigo-600 leading-4"
                    text={
                      getInspectionProgressStatusLabel({
                        t,
                        status: entry?.progressStatus,
                      }) || '--'
                    }
                  />
                  {isSubProgressStatusCompleted && (
                    <div className="mt-0.75 flex justify-center">
                      <div
                        className={twMerge(
                          'text-xs text-purple-5433FF/60 font-medium',
                          subProgressStatus?.className
                        )}
                      >
                        {subProgressStatus?.text}
                      </div>
                    </div>
                  )}
                </>
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:view')}
                    leftIconClassName="ki-eye !text-[17.5px]"
                    to={`/vehicule-check/view/${entry?.id}`}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
